import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import type { FeedbackClientContext } from '@ruta/shared';
import type { MentionOption } from '@/lib/api/assistantMentions';
import { useRutaAiAccess } from '@/hooks/useRutaAiAccess';
import { Popover } from '@/components/ui/popover';
import { captureWebFeedbackContext } from '@/lib/feedbackContext';
import type { RutaAiFraming } from '@/lib/assistantFraming';

export type { RutaAiFraming };

/**
 * Tailwind `md` — the header sparkle (PopoverAnchor) only mounts at md+.
 * Opening as a popover below that paints a blank overlay (RUT-3910).
 */
export const RUTA_AI_POPOVER_MIN_WIDTH = 768;

/** Same query Tailwind uses for `md` (`min-width: 768px`). */
export const RUTA_AI_NARROW_QUERY = `(max-width: ${RUTA_AI_POPOVER_MIN_WIDTH - 1}px)`;

export function shouldDetachRutaAiForViewport(width?: number): boolean {
  if (typeof width === 'number') {
    return width < RUTA_AI_POPOVER_MIN_WIDTH;
  }
  if (typeof window === 'undefined') return false;
  // iOS can report a wide layout innerWidth while CSS (and the mobile header)
  // still uses the visual viewport. Any of these saying "narrow" means the
  // sparkle PopoverAnchor is not mounted — detach instead of aligning to (0,0).
  if (window.innerWidth < RUTA_AI_POPOVER_MIN_WIDTH) return true;
  if (window.visualViewport && window.visualViewport.width < RUTA_AI_POPOVER_MIN_WIDTH) {
    return true;
  }
  if (typeof window.matchMedia === 'function' && window.matchMedia(RUTA_AI_NARROW_QUERY).matches) {
    return true;
  }
  return false;
}

function capturePageContext(): FeedbackClientContext | null {
  if (typeof window === 'undefined') return null;
  return captureWebFeedbackContext(
    'webapp',
    window.location.pathname + window.location.search,
    document.title,
  );
}

export interface OpenAiOptions {
  /** Door that opened the panel — Ask vs Feedback starter copy (RUT-3902). */
  framing?: RutaAiFraming;
}

/** A prompt to auto-send in a fresh conversation when the dock opens — lets a
 *  surface (e.g. a flagged visit) hand Ruta AI its context. */
export interface RutaAiSeed {
  prompt: string;
  /**
   * Short label shown in the chat bubble instead of `prompt`. Use when the seed
   * is a long drafting brief the model needs but the user should not have to read.
   */
  displayPrompt?: string;
  /** Entities to attach to the seeded message so the assistant is grounded on
   *  the exact target (e.g. the flagged visit) without a raw id in the prompt. */
  references?: MentionOption[];
  /**
   * When set, assistant replies offer a button that hands the final text back
   * to a host field (e.g. customer message composer) after a back-and-forth.
   */
  onApply?: (text: string) => void;
  /** Label for the apply button — defaults to "Use this". */
  applyLabel?: string;
}

/** Host that can receive a drafted text from the assistant mid-session. */
export interface RutaAiApplyDraft {
  onApply: (text: string) => void;
  label: string;
}

/** Anchored under the header sparkle vs free-floating (draggable) panel. */
export type RutaAiMode = 'popover' | 'detached';

interface RutaAiContextValue {
  open: boolean;
  /** How the panel is currently shown. Opens as `popover`; user can detach. */
  mode: RutaAiMode;
  /** Pending seed for the popover to auto-submit on open; cleared via clearSeed. */
  seed: RutaAiSeed | null;
  /**
   * When non-null, the panel is helping a host field (message composer, etc.)
   * and each assistant reply can be applied back.
   */
  applyDraft: RutaAiApplyDraft | null;
  /** Which Staff door opened the panel — drives greeting / placeholder / chips. */
  framing: RutaAiFraming;
  /** Page context captured at open (RUT-2602), kept for how-to and later file. */
  pageContext: FeedbackClientContext | null;
  openAi: (seed?: RutaAiSeed, options?: OpenAiOptions) => void;
  closeAi: () => void;
  toggleAi: () => void;
  /** Promote the anchored popover into the free-floating detached panel. */
  detachAi: () => void;
  /** Re-anchor the free-floating panel under the header sparkle. */
  dockAi: () => void;
  clearSeed: () => void;
}

const RutaAiContext = createContext<RutaAiContextValue | null>(null);

// Shared open-state for the global Ruta AI surface (header trigger + panel live far
// apart in the tree). The Radix Popover root lives here so the trigger can be a
// PopoverAnchor deep in the header while content portals from RutaAiPopover.
export function RutaAiProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<RutaAiMode>('popover');
  const [seed, setSeed] = useState<RutaAiSeed | null>(null);
  const [applyDraft, setApplyDraft] = useState<RutaAiApplyDraft | null>(null);
  const [framing, setFraming] = useState<RutaAiFraming>('ask');
  const [pageContext, setPageContext] = useState<FeedbackClientContext | null>(null);
  // Synchronous mirror so Popover onOpenChange can tell "user dismissed" from
  // "we flipped to detached and controlled-open went false".
  const modeRef = useRef<RutaAiMode>('popover');
  // Opening from another popover (suggestions, etc.) fires a same-gesture
  // outside-click that Radix reports as dismiss. Ignore that window.
  const ignoreDismissUntilRef = useRef(0);

  const openAi = useCallback((nextSeed?: RutaAiSeed, options?: OpenAiOptions) => {
    ignoreDismissUntilRef.current = Date.now() + 500;
    // Mobile header never mounts RutaAiTrigger (the PopoverAnchor). Popover
    // mode with no visible anchor paints a blank overlay — same path Tools →
    // Ask already takes by calling detachAi().
    if (shouldDetachRutaAiForViewport()) {
      modeRef.current = 'detached';
      setMode('detached');
    }
    setOpen(true);
    setPageContext(capturePageContext());
    if (options?.framing) {
      setFraming(options.framing);
    } else if (!nextSeed) {
      setFraming('ask');
    }
    if (nextSeed) {
      setSeed(nextSeed);
      // Capture apply target for the whole session (seed is cleared after auto-send).
      setApplyDraft(
        nextSeed.onApply
          ? { onApply: nextSeed.onApply, label: nextSeed.applyLabel ?? 'Use this' }
          : null,
      );
    }
  }, []);
  const closeAi = useCallback(() => {
    setOpen(false);
    modeRef.current = 'popover';
    setMode('popover');
    setApplyDraft(null);
  }, []);
  const toggleAi = useCallback(() => {
    setOpen((v) => {
      if (v) {
        modeRef.current = 'popover';
        setMode('popover');
        setApplyDraft(null);
        return false;
      }
      if (shouldDetachRutaAiForViewport()) {
        modeRef.current = 'detached';
        setMode('detached');
      }
      return true;
    });
  }, []);
  const detachAi = useCallback(() => {
    modeRef.current = 'detached';
    setMode('detached');
    setOpen(true);
  }, []);
  const dockAi = useCallback(() => {
    // No sparkle to dock to on phone width — stay detached so the panel
    // does not vanish the way Profile → Feedback used to.
    if (shouldDetachRutaAiForViewport()) {
      modeRef.current = 'detached';
      setMode('detached');
      setOpen(true);
      return;
    }
    modeRef.current = 'popover';
    setMode('popover');
    setOpen(true);
  }, []);
  const clearSeed = useCallback(() => setSeed(null), []);
  const canUseRutaAi = useRutaAiAccess();

  useEffect(() => {
    // Owner/Admin-only + gated off in prod: no Cmd-K entry otherwise.
    if (!canUseRutaAi) return;
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => {
          if (v) {
            modeRef.current = 'popover';
            setMode('popover');
            setApplyDraft(null);
            return false;
          }
          if (shouldDetachRutaAiForViewport()) {
            modeRef.current = 'detached';
            setMode('detached');
          }
          setFraming('ask');
          setPageContext(capturePageContext());
          return true;
        });
      } else if (e.key === 'Escape') {
        setOpen(false);
        modeRef.current = 'popover';
        setMode('popover');
        setApplyDraft(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canUseRutaAi]);

  return (
    <RutaAiContext.Provider
      value={{
        open,
        mode,
        seed,
        applyDraft,
        framing,
        pageContext,
        openAi,
        closeAi,
        toggleAi,
        detachAi,
        dockAi,
        clearSeed,
      }}
    >
      {/* modal=false keeps the app interactive behind the anchored chat (same
          non-blocking spirit as the detached panel). Anchor lives on the header
          sparkle; content portals from RutaAiPopover. */}
      <Popover
        modal={false}
        open={open && mode === 'popover'}
        onOpenChange={(next) => {
          if (next) {
            setOpen(true);
            return;
          }
          if (Date.now() < ignoreDismissUntilRef.current) return;
          // Detaching flips mode first (modeRef already 'detached') and drives
          // controlled open false — ignore that so we don't wipe the session.
          if (modeRef.current === 'popover') {
            setOpen(false);
          }
        }}
      >
        {children}
      </Popover>
    </RutaAiContext.Provider>
  );
}

export function useRutaAi() {
  const ctx = useContext(RutaAiContext);
  if (!ctx) throw new Error('useRutaAi must be used within a RutaAiProvider');
  return ctx;
}
